import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import SalesOrderLineEditModal from '@totem/components/salesOrders/edits/SalesOrderLineEditModal';
import SalesOrderLineHistoryModal from '@totem/components/salesOrders/history/SalesOrderLineHistoryModal';
import SalesOrdersContext from '@totem/components/salesOrders/list/SalesOrdersContext';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DateStringToUTCDate } from '@totem/utilities/dateUtilities';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Active':
    case 'Project Complete':
      return 'success';
    case 'Expired':
    case 'Canceled':
      return 'error';
    case 'Pending':
    case 'Pending Dates':
    case 'Provisioning':
      return 'warning';
    case 'Information':
      return 'blue';
    default:
      return 'default';
  }
};

const SalesOrdersTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { lines, loading, totalRecords, refetch } =
    useContext(SalesOrdersContext);
  const { handleTableChange, input, getFilterOptions } =
    useContext(InputContext);
  const [editLine, setEditLine] = useState<SalesOrderLine | null>(null);
  const [historyLine, setHistoryLine] = useState<SalesOrderLine | null>(null);

  const isEditor = permissions.contractsEditor.hasPermissions(userProfile);

  const columns: ColumnProps<SalesOrderLine>[] = [
    {
      title: 'Sales Order',
      key: 'salesOrder',
      sorter: true,
      render: (_, record: SalesOrderLine) =>
        record.nsSalesOrder?.tranId ? (
          <Link
            to={`/dashboard/salesOrder/${encodeURIComponent(record.nsSalesOrder.tranId)}`}
          >
            {record.nsSalesOrder.tranId}
          </Link>
        ) : null,
      width: '120px',
    },
    {
      title: 'Line',
      key: 'line',
      sorter: true,
      render: (_, record: SalesOrderLine) => (
        <span>{record.nsSalesOrderLine?.line}</span>
      ),
      width: '60px',
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: '90px',
      render: (_, record: SalesOrderLine) => (
        <span>
          {isNotNull(record.nsSalesOrderLine?.custcol24) &&
          record.nsSalesOrderLine?.custcol24 > 0
            ? record.nsSalesOrderLine?.custcol24
            : record.nsSalesOrderLine?.quantity}
        </span>
      ),
    },
    {
      title: 'Description',
      key: 'description',
      sorter: true,
      render: (_, record: SalesOrderLine) => (
        <span>{record.nsSalesOrderLine?.description}</span>
      ),
    },
    {
      title: 'Region',
      key: 'region',
      sorter: true,
      filters: getFilterOptions('region'),
      filterMultiple: true,
      render: (_, record: SalesOrderLine) => <span>{record.region?.name}</span>,
    },
    {
      title: 'Building',
      key: 'building',
      sorter: true,
      filters: getFilterOptions('building'),
      filterMultiple: true,
      render: (_, record: SalesOrderLine) =>
        record.building?.id && record.building.id !== emptyId ? (
          <span>{record.building.name}</span>
        ) : (
          <span style={{ color: 'red' }}>
            {record.nsShippingAddress?.addr1}
          </span>
        ),
    },
    {
      title: 'Devices',
      key: 'devices',
      render: (_, record: SalesOrderLine) => (
        <span>
          {(record.devices ?? []).map((dev) => (
            <span key={dev.id}>
              {dev.name}
              <br />
            </span>
          ))}
        </span>
      ),
    },
    {
      title: 'Services',
      key: 'service',
      filters: getFilterOptions('service'),
      filterMultiple: true,
      render: (_, record: SalesOrderLine) => (
        <span>
          {(record.services ?? []).map((svc) => (
            <span key={svc.id}>
              {svc.name}
              <br />
            </span>
          ))}
        </span>
      ),
    },
    {
      title: 'Start Date',
      key: 'startDate',
      sorter: true,
      render: (_, record: SalesOrderLine) => (
        <span>{DateStringToUTCDate(record.startDate)}</span>
      ),
      width: '100px',
    },
    {
      title: 'End Date',
      key: 'endDate',
      sorter: true,
      render: (_, record: SalesOrderLine) => (
        <span>{DateStringToUTCDate(record.endDate)}</span>
      ),
      width: '100px',
    },
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      filters: getFilterOptions('status'),
      filterMultiple: true,
      render: (_, record: SalesOrderLine) => (
        <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
      ),
      width: '120px',
    },
    {
      title: '',
      key: 'actions',
      render: (_, record: SalesOrderLine) => (
        <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
          <Tooltip title="View Change History">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryLine(record)}
            />
          </Tooltip>
          {isEditor && (
            <IBTooltip content={permissions.contractsEditor.tooltip}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => setEditLine(record)}
              />
            </IBTooltip>
          )}
        </div>
      ),
      width: isEditor ? '90px' : '50px',
    },
  ];

  return (
    <>
      <IBTable
        columns={columns}
        dataSource={lines}
        loading={loading}
        rowKey={(record) => record.id}
        onChange={handleTableChange}
        pagination={{
          current: input.pagination.page,
          pageSize: input.pagination.pageSize,
          total: totalRecords,
          showSizeChanger: true,
        }}
      />
      {historyLine !== null && (
        <SalesOrderLineHistoryModal
          salesOrderLine={historyLine}
          onClose={() => setHistoryLine(null)}
        />
      )}
      {editLine !== null && (
        <SalesOrderLineEditModal
          salesOrderLine={editLine}
          onClose={(updated) => {
            setEditLine(null);
            if (updated) {
              refetch();
            }
          }}
        />
      )}
    </>
  );
};

export default SalesOrdersTable;
